/**
 * Where a product card lands.
 *
 * Every card on Home carries a route, and a route that goes nowhere is worse
 * than no link: `/robotics` falling through to the catch-all would drop
 * somebody on the CenoX Business dashboard with no explanation of why.
 *
 * So each product's slug resolves here, and this reads the SAME server
 * answer Home does. For a product the workspace holds and has initialized, it
 * hands over to that product. For everything else it says which of the several
 * different "no" this is — not sold yet, not bought, not set up, setting up, or
 * failed — because those need different things from the reader.
 *
 * It provisions nothing and it shows nothing derived from a locked product's
 * data. The name, the description and a route to get it: that is all a product
 * this workspace has not paid for is allowed to say.
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PRODUCT_ACTIONS, statusLine, usePlatformProducts } from '../../lib/platformProducts';
import { ProductCard } from '../../components/platform/ProductCard';
import { NoWorkspaceNotice } from '../../components/platform/NoWorkspaceNotice';
import { useAuth } from '../../lib/auth';
import { CenoxShell } from '../../layouts/CenoxShell';
import { Button, Card, ErrorState, Spinner } from '../../components/ui';

/**
 * Where an entitled, initialized product actually opens.
 *
 * A product missing from this map has no screen yet, and this page says so
 * rather than sending somebody somewhere that is not it.
 */
const PRODUCT_ENTRY: Record<string, string> = {
  // CenoX Business has its own route and never reaches this page. Entries
  // appear here as products get screens of their own.
};

export function ProductPage(): ReactNode {
  /**
   * The slug comes from the path, not from a route parameter.
   *
   * The product routes are enumerated literally rather than declared as
   * `/:slug` — a wildcard there would swallow every unknown path in the
   * application, including ones the Business shell owns — so there is no
   * parameter to read.
   */
  const { pathname } = useLocation();
  /*
    The FIRST segment. CenoX AI has sections beneath it — `/ai/agents` — and a
    workspace that is not entitled to CenoX AI lands here for those too, so the
    product has to be read from the head of the path rather than the whole of
    it.
  */
  const slug = pathname.replace(/^\/+/, '').split('/')[0] ?? '';
  const navigate = useNavigate();
  const { business } = useAuth();
  const products = usePlatformProducts();

  const product = products.data?.data.find((p) => p.routeSlug === slug);

  // An entitled, initialized product is not a page — it is the product.
  if (product && product.availableAction === 'open') {
    const entry = PRODUCT_ENTRY[product.product];
    if (entry) return <Navigate to={entry} replace />;
  }

  return (
    <CenoxShell>
      <Button
        variant="ghost"
        size="sm"
        icon={<ArrowLeft className="size-4" />}
        onClick={() => navigate('/')}
      >
        CenoX Home
      </Button>

      <div className="mt-4">
        {products.isPending && (
          <Card>
            <Spinner label="Loading" />
          </Card>
        )}

        {products.isError && (
          <ErrorState
            message="Could not load this product's status. This is a connection problem, not a change to what you have access to."
            onRetry={() => void products.refetch()}
          />
        )}

        {products.data && !product && (
          <Card>
            <p className="text-sm text-slate-900">That is not a CenoX product.</p>
            <p className="mt-1 text-sm text-slate-500">
              Check the address, or go back to CenoX Home for the full list.
            </p>
          </Card>
        )}

        {product && (
          <div className="max-w-xl">
            <ProductCard product={product} />

            {!business && product.catalogueStatus === 'available' ? (
              // The card already says it; this is where to go from here.
              <NoWorkspaceNotice className="mt-4" showMessage={false} />
            ) : (
              <p className="mt-4 text-sm text-slate-500">
                {product.availableAction === 'open'
                  ? 'This product is ready, but it does not have its own screen in CenoX yet.'
                  : statusLine(product, Boolean(business))}
              </p>
            )}

            {/*
              What pressing the button will do, said before it is pressed.
              Activation writes to the workspace, and somebody arriving here
              from a link deserves to know that before they act.
            */}
            {PRODUCT_ACTIONS[product.availableAction].kind === 'activate' && (
              <p className="mt-2 text-xs text-slate-400">
                Setting up runs once for this workspace. Running it again does nothing.
              </p>
            )}
          </div>
        )}
      </div>
    </CenoxShell>
  );
}
